import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * ChartEmptyState
 *
 * A visual placeholder component used to indicate an empty chart area when no data or sensors
 * are available. It displays a relevant message based on the type of empty state provided.
 *
 * Commonly used in charting dashboards to gracefully handle scenarios like:
 * - No sensors are currently enabled.
 * - The selected date range has no data to show.
 *
 * @component
 *
 * @param {ChartEmptyStateProps} props - Props to configure the empty state message and style.
 * @param {EMPTY_STATE_ENUM} props.type - The type of empty state to display. Determines the title and subtitle.
 *   - `EMPTY_STATE_ENUM.NO_SENSORS`: No sensors are enabled for this chart.
 *   - `EMPTY_STATE_ENUM.NO_DATA`: No data is available for the selected range.
 * @param {string} [props.className] - Optional additional CSS classes for layout customization.
 *
 * @returns {JSX.Element} A styled empty state container with title and subtitle messages.
 *
 * @example
 * <ChartEmptyState type={EMPTY_STATE_ENUM.NO_SENSORS} />
 *
 * @example
 * <ChartEmptyState type={EMPTY_STATE_ENUM.NO_DATA} className="mt-4 border-dashed" />
 */

export function ChartEmptyState({ type, className, dataLayer }: ChartEmptyStateProps): JSX.Element {
  const { t } = useTranslation();
  
  // Determine if this is an alarm section based on dataLayer
  const isAlarmSection = dataLayer === 'alarm' || dataLayer === 'alarms';
  
  // Get the appropriate empty state based on whether it's an alarm section
  const emptyStates = isAlarmSection ? getAlarmEmptyState(t) : getEmptyState(t);
  const emptyState = emptyStates[type] || emptyStates[EMPTY_STATE_ENUM.NO_DATA];
  
  const { title, subtitle } = emptyState;
  
  const displayTitle = title;

  return (
    <div
      className={cn(
        'flex-1 flex items-center justify-center bg-white dark:bg-[#52606d] rounded border border-gray-200 dark:border-[#2f3844] min-h-[300px] bg-no-repeat bg-contain bg-center',
        className
      )}
      // style={{ backgroundImage: `url(${emptyBg})` }}
    >
      <div className="relative z-10 flex flex-col items-center justify-center text-[#55687d] dark:text-[#8b98a5] h-full">
        <p className="text-center font-semibold">{displayTitle}</p>
        <p className="text-center text-xs mt-2">{subtitle}</p>
      </div>
    </div>
  );
}

/**
 * Enum to distinguish between different types of empty chart states.
 */
export enum EMPTY_STATE_ENUM {
  NO_SENSORS = 'no_sensors',
  NO_DATA = 'no_data',
}

const getEmptyState = (t: any): Record<EMPTY_STATE_ENUM, { title: string; subtitle: string }> => ({
  [EMPTY_STATE_ENUM.NO_SENSORS]: {
    title: t('sensorFusion.emptyState.noSensors.title'),
    subtitle: '',
  },
  [EMPTY_STATE_ENUM.NO_DATA]: {
    title: t('sensorFusion.emptyState.noData.title'),
    subtitle: t('sensorFusion.emptyState.noData.subtitle'),
  },
});

const getAlarmEmptyState = (t: any): Record<EMPTY_STATE_ENUM, { title: string; subtitle: string }> => ({
  [EMPTY_STATE_ENUM.NO_SENSORS]: {
    title: t('sensorFusion.emptyState.noAlarms.title'),
    subtitle: '',
  },
  [EMPTY_STATE_ENUM.NO_DATA]: {
    title: t('sensorFusion.emptyState.noData.title'),
    subtitle: t('sensorFusion.emptyState.noData.subtitle'),
  },
});

type ChartEmptyStateProps = {
  type: EMPTY_STATE_ENUM;
  className?: string;
  dataLayer?: string;
};
